import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  Badge,
  Box,
  Divider,
  FormGroup,
  Grid,
  IconButton,
  Typography,
  Rating,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Clear as ClearIcon, ExpandMore } from '@mui/icons-material';
import moment from 'moment';
import { daysList } from '../../../helpers/RescuesHelper';
import MinMaxTimePicker from '../../../components/MinMaxTimePicker';
import { largeDeliverisValuesList } from '../../../components/LargeDeliveriesSelector';

const ReceiverNeedsSelector = ({
  afterValue,
  beforeValue,
  handleAfterChange,
  handleBeforeChange,
  handleCheckboxChange,
  handleNeedsChange,
  handleLargeDeliveriesChange,
  onAccordionChange,
  disabled,
  expanded = false,
  selectedDays = [],
  categories = [],
  needs = [],
  badgeCounter = 0,
  selectedLargeDeliveries = {},
}) => {
  const [after, setAfter] = useState(afterValue || null);
  const [before, setBefore] = useState(beforeValue || null);

  const handleAfterTimeChange = ({ event, isValid }) => {
    if (isValid) {
      setAfter(event.format('HH:mm:00'));
    }
  };

  const handleAfterTimeUpdate = () => {
    handleAfterChange(after);
  };

  const clearAfter = () => {
    setAfter(null);
    handleAfterChange(null);
  };

  const handleBeforeTimeChange = ({ event, isValid }) => {
    if (isValid) {
      setBefore(event.format('HH:mm:00'));
    }
  };

  const handleBeforeTimeUpdate = () => {
    handleBeforeChange(before);
  };

  const clearBefore = () => {
    setBefore(null);
    handleBeforeChange(null);
  };

  return (
    <Accordion
      sx={{
        '&.Mui-expanded': {
          margin: 0,
        },
        '&:before': {
          display: 'none',
        },
      }}
      elevation={0}
      onChange={onAccordionChange}
      expanded={expanded}
    >
      <AccordionSummary
        sx={{
          position: 'relative',
          fontWeight: 'bold',
          height: '40px',
          minHeight: '40px',
          '&.Mui-expanded': {
            minHeight: '50px',
          },
        }}
        expandIcon={<ExpandMore />}
      >
        Agency Details
        <Badge
          sx={{
            position: 'absolute',
            right: 50,
            top: expanded ? 25 : 20,
            transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          }}
          badgeContent={badgeCounter}
          color="error"
        />
      </AccordionSummary>
      <Box px={2}>
        <Box mb={1}>
          <Typography variant="body2">Agency Hours</Typography>
        </Box>
        <Box>
          <FormGroup row>
            {daysList.map((day, dayIndex) => {
              const dayName = day.toLowerCase();
              return (
                <FormControlLabel
                  key={day}
                  sx={{ marginRight: 5 }}
                  control={
                    <Checkbox
                      name={dayName}
                      disabled={disabled}
                      sx={{ paddingRight: 1 }}
                      checked={!!selectedDays.find((d) => d === dayIndex.toString())}
                      onChange={handleCheckboxChange}
                      value={dayIndex}
                      size="small"
                      color="secondary"
                    />
                  }
                  label={<Typography variant="caption">{day}</Typography>}
                />
              );
            })}
          </FormGroup>
        </Box>

        <Box display="flex" gap="16px" py={1}>
          <Box display="flex">
            <MinMaxTimePicker
              sx={{
                '& input': {
                  padding: '6px 14px',
                },
              }}
              inputVariant="outlined"
              size="small"
              label="After"
              disabled={disabled}
              value={afterValue ? moment(afterValue, 'HH:mm:ss') : null}
              maxTime={before ? moment(before, 'HH:mm:ss') : moment().set({ hour: '23', minute: '55', second: '00' })}
              onTimeChange={handleAfterTimeChange}
              onClose={handleAfterTimeUpdate}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <IconButton disabled={disabled} size="small" onClick={clearAfter}>
              <ClearIcon size="small" onClick={clearAfter} />
            </IconButton>
          </Box>

          <Box display="flex">
            <MinMaxTimePicker
              sx={{
                '& input': {
                  padding: '6px 14px',
                },
              }}
              inputVariant="outlined"
              size="small"
              label="Before"
              disabled={disabled}
              value={beforeValue ? moment(beforeValue, 'HH:mm:ss') : null}
              minTime={after ? moment(after, 'HH:mm:ss') : moment().set({ hour: '00', minute: '05', second: '00' })}
              onTimeChange={handleBeforeTimeChange}
              onClose={handleBeforeTimeUpdate}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <IconButton disabled={disabled} size="small" onClick={clearBefore}>
              <ClearIcon />
            </IconButton>
          </Box>
        </Box>
        {selectedDays.length > 0 && (
          <Box>
            <Typography variant="caption" color="textSecondary">
              Agencies without hours will not appear. Add hours on the Agency Profile page.
            </Typography>
          </Box>
        )}
        <Divider />

        <Box my={1}>
          <Typography variant="body2">Agency Needs</Typography>
        </Box>

        <Box>
          <Typography variant="caption">Select a rating to match food to an agency based on their needs.</Typography>
        </Box>
        <Grid container>
          {categories.map((category) => {
            const need = needs.find((n) => n.name === category.name);
            return (
              <Grid item xs={4} key={category.id}>
                <Typography variant="body2" component="legend">
                  {category.name !== 'Prepared/Ready to Eat' ? category.name : 'Prepared'}
                </Typography>
                <Rating
                  disabled={disabled}
                  name={category.name}
                  size="small"
                  value={need ? need.rating : null}
                  onChange={(e, newValue) => {
                    handleNeedsChange({
                      name: category.name,
                      rating: newValue !== null ? newValue : 0,
                      id: category.id,
                    });
                  }}
                />
              </Grid>
            )})}
        </Grid>
        <Divider />

        <Box my={1}>
          <Typography variant="body2">Large Deliveries</Typography>
        </Box>

        <FormGroup>
          <FormControlLabel
            label={largeDeliverisValuesList.accessToPalletJack.label}
            control={
              <Checkbox
                disabled={disabled}
                checked={!!selectedLargeDeliveries?.[largeDeliverisValuesList.accessToPalletJack.value]?.checked}
                onChange={(e) =>
                  handleLargeDeliveriesChange(e.target.checked, largeDeliverisValuesList.accessToPalletJack.value)
                }
              />
            }
          />
          <FormControlLabel
            label={largeDeliverisValuesList.accessToForklift.label}
            control={
              <Checkbox
                disabled={disabled}
                checked={!!selectedLargeDeliveries?.[largeDeliverisValuesList.accessToForklift.value]?.checked}
                onChange={(e) =>
                  handleLargeDeliveriesChange(e.target.checked, largeDeliverisValuesList.accessToForklift.value)
                }
              />
            }
          />
          <FormControlLabel
            disabled={disabled}
            label={largeDeliverisValuesList.highCapacityColdStorage.label}
            control={
              <Checkbox
                checked={!!selectedLargeDeliveries?.[largeDeliverisValuesList.highCapacityColdStorage.value]?.checked}
                onChange={(e) =>
                  handleLargeDeliveriesChange(e.target.checked, largeDeliverisValuesList.highCapacityColdStorage.value)
                }
              />
            }
          />
          <FormControlLabel
            disabled={disabled}
            label={largeDeliverisValuesList.highCapacityStorage.label}
            control={
              <Checkbox
                checked={!!selectedLargeDeliveries?.[largeDeliverisValuesList.highCapacityStorage.value]?.checked}
                onChange={(e) =>
                  handleLargeDeliveriesChange(e.target.checked, largeDeliverisValuesList.highCapacityStorage.value)
                }
              />
            }
          />
          <FormControlLabel
            disabled={disabled}
            label={largeDeliverisValuesList.onsiteLoadingDock.label}
            control={
              <Checkbox
                checked={!!selectedLargeDeliveries?.[largeDeliverisValuesList.onsiteLoadingDock.value]?.checked}
                onChange={(e) =>
                  handleLargeDeliveriesChange(e.target.checked, largeDeliverisValuesList.onsiteLoadingDock.value)
                }
              />
            }
          />
        </FormGroup>
      </Box>
      <Divider />
    </Accordion>
  );
};

export default ReceiverNeedsSelector;
